import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tag, Btn, Stepper } from "../components/index.js";
import { Icon } from "../components/Icon.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { useSurface } from "../lib/useSurface.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals } from "../lib/api.js";

const COMMANDS = ['"次へ"', '"戻る"', '"もう一回"', '"一時停止"'];

/** Hands-free playback — voice/foot-switch operable step playback (field). */
export function Handsfree() {
  useSurface("field");
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { data, loading, error, offline, reload } = useAsync(() => manuals.get(id), [id]);
  const steps = data?.steps ?? [];
  const [idx, setIdx] = useState(0);
  const step = steps[idx];

  return (
    <div className="shell">
      <header className="appbar">
        <button className="btn btn--ghost" aria-label="戻る" onClick={() => navigate(`/viewer/${id}`)} style={{ minHeight: 56, minWidth: 56, padding: "8px 14px", borderRadius: 12 }}>
          <Icon name="chevron" size={20} style={{ transform: "rotate(90deg)" }} />
        </button>
        <div className="grow">
          <div className="kicker">ハンズフリー再生 — {data?.title ?? "マニュアル"}</div>
          {steps.length ? <Stepper total={steps.length} current={idx + 1} /> : null}
        </div>
        <span className="statuspill">
          <span className="sdot sdot--hi" aria-hidden="true" />
          音声操作 ON
        </span>
      </header>

      {loading ? (
        <LoadingState label="マニュアルを読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : !step ? (
        <EmptyState icon={<Icon name="empty-box" />} title="再生できる手順がありません">
          このマニュアルにはまだ手順がありません。
        </EmptyState>
      ) : (
        <>
          <main className="hf" style={hfStyle}>
            <Tag hi>STEP {String(step.order).padStart(2, "0")} · 読み上げ中</Tag>
            <h1 className="jp reveal d1" style={{ fontFamily: "var(--font-display)", fontSize: "clamp(26px,6vw,42px)" }}>
              {step.title}
            </h1>
            <div className="wave reveal d1" aria-hidden="true" style={{ height: 46 }}>
              {Array.from({ length: 11 }, (_, i) => (
                <i key={i} />
              ))}
            </div>
            <p className="jp reveal d2" style={{ maxWidth: 520, fontSize: 18, color: "var(--ink)" }}>
              {step.caption}
            </p>

            <div className="mic reveal d2" style={micStyle}>
              <div className="core" style={{ width: 84, height: 84, borderRadius: "50%", background: "var(--hi)", color: "#10130a", display: "grid", placeItems: "center", fontSize: 34 }}>
                <Icon name="mic" size={40} />
              </div>
            </div>

            <div className="row reveal d3" style={{ flexWrap: "wrap", justifyContent: "center" }}>
              {COMMANDS.map((c) => (
                <span key={c} className="cmd" style={cmdStyle}>
                  {c}
                </span>
              ))}
            </div>
            <p className="jp" style={{ color: "var(--muted)", fontSize: 13 }}>
              手が塞がっていても、声だけで操作。フットスイッチにも対応。
            </p>
          </main>

          <div className="actionbar">
            <Btn variant="ghost" lg disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} icon={<Icon name="chevron" size={18} style={{ transform: "rotate(90deg)" }} />}>
              前
            </Btn>
            <Btn variant="default" lg className="grow" disabled title="準備中" aria-disabled="true" icon={<Icon name="pause" size={18} />}>
              一時停止
            </Btn>
            <Btn variant="hi" lg disabled={idx >= steps.length - 1} onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))}>
              次<Icon name="chevron" size={18} style={{ transform: "rotate(-90deg)", marginLeft: 6 }} />
            </Btn>
          </div>
        </>
      )}
    </div>
  );
}

const hfStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "30px 24px",
  gap: 20,
} as const;
const micStyle = {
  width: 120,
  height: 120,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  position: "relative",
  background: "radial-gradient(circle,rgba(203,255,61,.18),transparent 70%)",
} as const;
const cmdStyle = {
  display: "inline-flex",
  gap: 8,
  alignItems: "center",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  padding: "8px 14px",
  border: "1px solid var(--line-2)",
  borderRadius: 999,
} as const;
